import React, { FC, useEffect, useRef, useState } from "react";
import { useRemoteComponent } from "@bookingcom/remote-component";
// eslint-disable-next-line import/no-deprecated
import { useStore } from "store";

import useRouteName from "hooks/useRouteName";
import useEvents from "hooks/useEvents";
import useClientMetrics from "hooks/useClientMetrics";
import { createPortal } from "react-dom";

const REMOTE_COMPONENT_PATH_XM_FLIGHTS = "/SurveyTracker/Flights/Universal";

export const REMOTE_COMPONENT_XM_CONTAINER_DOM_ID = "FlightsXMContainer";

const XM_SURVEY_PATH_BY_ROUTE_NAME: Record<string, string> = {
  confirmation: "FlightsConfirmationPage",
  "pb-order-details": "FlightsBookingDetailsPage",
  "checkout-payment": "FlightsCheckoutPage"
};

const SurveyTrackerFlightsUniversal: FC = () => {
  const routeName = useRouteName();
  // eslint-disable-next-line import/no-deprecated
  const store = useStore();
  const { order } = store.order;
  const findContainerTimer = useRef(0); // find survey dom container on current webpage
  const containerDOMRef = useRef<HTMLElement | null>(null);
  const [containerFound, setContainerFound] = useState(false);

  const { grumble, squeak } = useEvents();
  const { trackClientMetric } = useClientMetrics();

  // set page name to control show/hidden
  useEffect(() => {
    const sendPageNameInfo = () => {
      if (routeName && containerFound) {
        document.dispatchEvent(
          new CustomEvent("CAPLA_EVENT_flights_slot_info", {
            detail: {
              page: XM_SURVEY_PATH_BY_ROUTE_NAME[routeName] || "",
              region: containerDOMRef?.current?.getAttribute("data-region")
            }
          })
        );
      }
    };
    sendPageNameInfo();
    document.addEventListener("CAPLA_EVENT_xm_remote_loader_init", sendPageNameInfo);
    return () => {
      document.removeEventListener("CAPLA_EVENT_xm_remote_loader_init", sendPageNameInfo);
    };
  }, [routeName, containerFound]);

  // Log
  useEffect(() => {
    const xmSqueakLogs = (e: any) => {
      try {
        const data = e.detail;
        if (data) {
          let squeakString = "xm";
          data.platform && (squeakString += `_${data.platform}`);
          data.page && (squeakString += `_${data.page}`);
          data.surveyName && (squeakString += `_${data.surveyName}`);
          data.action && (squeakString += `_${data.action}`);
          squeak(squeakString);
          trackClientMetric(squeakString.toLowerCase() as any);
        }
        // eslint-disable-next-line flights/no-empty-catch
      } catch (e) {}
    };
    document.addEventListener("CAPLA_EVENT_survey-tracker-component-service_CLIENT_SIDE_SQUEAK", xmSqueakLogs);
    return () => {
      document.removeEventListener("CAPLA_EVENT_survey-tracker-component-service_CLIENT_SIDE_SQUEAK", xmSqueakLogs);
    };
  }, [squeak, trackClientMetric]);

  // communicate with RemoteComponent via CustomEvent
  useEffect(() => {
    const sendOrderInfo = () => {
      if (order) {
        const { orderStatus, orderId } = order;
        document.dispatchEvent(
          new CustomEvent("CAPLA_EVENT_xm_remote_component_order_info", { detail: { orderId, orderStatus } })
        );
      }
    };
    sendOrderInfo();
    document.addEventListener("CAPLA_EVENT_xm_component_init", sendOrderInfo);
    return () => {
      document.removeEventListener("CAPLA_EVENT_xm_component_init", sendOrderInfo);
    };
  }, [order]);

  // // ET custom goal
  // useEffect(() => {
  //   const xmETCustomGoal = (e: any) => {
  //     const data = e.detail;
  //     if (data && expTagName) {
  //       customGoal(expTagName, data.customGoal);
  //     }
  //   };
  //   document.addEventListener("CAPLA_EVENT_et_custom_goal", xmETCustomGoal);
  //   return () => {
  //     document.removeEventListener("CAPLA_EVENT_et_custom_goal", xmETCustomGoal);
  //   };
  // }, [expTagName]);

  const { Component, isLoading, error } = useRemoteComponent({
    project: "web-shell",
    service: "header-mfe",
    path: REMOTE_COMPONENT_PATH_XM_FLIGHTS,
    type: "capla"
  });

  useEffect(() => {
    if (error) {
      grumble(" RC error: Survey tracker wrapper", { routeName, error: error.toString() });
    }
  }, [error, grumble, routeName]);

  const getContainerDom = () => {
    const elm = window?.document?.getElementById(REMOTE_COMPONENT_XM_CONTAINER_DOM_ID);
    if (elm) {
      window?.clearInterval(findContainerTimer.current);
      containerDOMRef.current = elm;
      findContainerTimer.current = 0;
      setContainerFound(true);
    }
    return elm;
  };

  useEffect(() => {
    setContainerFound(false);
    if (routeName in XM_SURVEY_PATH_BY_ROUTE_NAME) {
      findContainerTimer.current = window.setInterval(getContainerDom, 200);
    }
    return () => {
      findContainerTimer.current && window.clearInterval(findContainerTimer.current);
    };
  }, [routeName]);

  const renderSurvey = () => {
    if (typeof window === "undefined") return null; // avoid SSR null pointer
    if (containerFound && containerDOMRef?.current && !isLoading) {
      return createPortal(<Component />, containerDOMRef.current);
    }
    return null;
  };

  return renderSurvey();
};

interface SurveyTrackerLoaderProps {
  region?: string;
}

export const SurveyTrackerLoader: FC<SurveyTrackerLoaderProps> = (props) => {
  const routeName = useRouteName();
  if (routeName in XM_SURVEY_PATH_BY_ROUTE_NAME) {
    return <div id={REMOTE_COMPONENT_XM_CONTAINER_DOM_ID} data-region={props.region}></div>;
  }
  return null;
};

export default SurveyTrackerFlightsUniversal;
